import { Component, ElementRef, ErrorHandler, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { WebPlotDigitizerComponent } from '../../web-plot-digitizer/web-plot-digitizer.component';
import { ReactiveFormsModule, FormsModule, NgForm, FormBuilder } from '@angular/forms';
import { Chart } from 'chart.js/auto';
import moment from 'moment';

import { TireSizeCalculatorComponent } from '../../components/tire-size-calculator/tire-size-calculator.component';
import { EventBroadcastService } from '../../services/event-broadcast.service';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { SpeedUnits } from '../../models/speed-units';
import { PowerUnits } from '../../models/power-units';
import { TorqueUnits } from '../../models/torque-units';
import { WeightUnits } from '../../models/weight-units';
import { InputRangeDirective } from '../../directives/input-range.directive';
import { InputSpinnerComponent } from '../../components/input-spinner/input-spinner.component';
import { DigitizerService } from '../../services/digitizer.service';
import { Drivetrain } from '../../models/calculatedSettings';
import { ChartService } from '../../services/chart.service';
import { ProtuneApiService } from '../../services/protune-api.service';
import { UserPreferences } from '../../models/user-preferences';
import { ChartJsDirective } from '../../directives/chart-js.directive';
import { Suspension } from '../../models/Suspension';
import { Vehicle } from '../../models/vehicle';
import { SuspensionSettings } from '../../models/suspension-settings';
import { DrivetrainSettings } from '../../models/drivetrain-settings';
import { CarTrackSettings } from '../../models/car-track-settings';
import { NgbAccordionModule, NgbDatepickerModule, NgbDropdownModule, NgbModal, NgbPopover, NgbTooltip, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { ErrorModalComponent } from '../../components/error-modal/error-modal.component';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import { Router, RouterLink } from '@angular/router';
import { HelpText } from '../../types/help-text';
import { ScreenshotHelpModalComponent } from '../../components/screenshot-help-modal/screenshot-help-modal.component';
import { AuthService, AppSubscription } from '../../services/auth.service';
import { SavedSettingsModalComponent, SavedSettingsMode } from '../../components/saved-settings-modal/saved-settings-modal.component';
import { SavedSettings } from '../../models/saved-settings';
import { SubscriptionLevels } from '../../models/subscription-levels';
import { Subject, takeUntil } from 'rxjs';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { LengthUnits } from '../../models/length-units';
import { DriveTypes } from '../../models/drive-types';
import { NotificationModalComponent } from '../../components/notification-modal/notification-modal.component';
import { GT7ChatWidgetComponent } from '../../components/gt7-chat-widget/gt7-chat-widget.component';
import { ProcessImageModalComponent } from '../../components/process-image-results-modal/process-image-modal.component';
import { VehicleSelectorComponent } from '../../components/vehicle-selector/vehicle-selector.component';
import { TrackSelectorComponent } from '../../components/track-selector/track-selector.component';
import { ProcessImageResult } from '../../models/process-image-result';
import { LbftTorquePipe } from '../../pipes/lbft-to-torque.pipe';
import { HpToPowerPipe } from '../../pipes/hp-to-power.pipe';
import { HomeTourComponent } from '../../components/home-tour/home-tour.component';
import { GearingMode } from '../../models/gearing-mode';

const wpd = (window as any).wpd;
declare let gtag: Function;

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, WebPlotDigitizerComponent, FormsModule,
    ReactiveFormsModule, TireSizeCalculatorComponent,
    InputRangeDirective, InputSpinnerComponent,
    ChartJsDirective, NgbDatepickerModule, NgbPopover, NgbAccordionModule,
    NgbTooltip, NgbDropdownModule, NgbTypeaheadModule, RouterLink, GT7ChatWidgetComponent,
    VehicleSelectorComponent, TrackSelectorComponent, LbftTorquePipe, HpToPowerPipe,
    HomeTourComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnDestroy, OnInit {
  carTrackSettings: CarTrackSettings = new CarTrackSettings();
  vehicles: Array<Vehicle>;
  screenshotFile: File | null;
  screenshotFilename: string;
  gearRatios: Record<number, number>;
  userPreferences: UserPreferences;
  dynoChartConfig: any;
  shiftPointsChartConfig: any;
  tractiveForceChartConfig: any;
  suspension: Suspension;
  drivetrain: Drivetrain;
  suspensionSettings: SuspensionSettings = new SuspensionSettings();
  drivetrainSettings: DrivetrainSettings = new DrivetrainSettings();
  showDrivetrainSettings: boolean = true;
  showSuspensionSettings: boolean = true;
  showSuspension: boolean = true;
  showDrivetrain: boolean = false;
  activeTab: string = 'car-track';
  subscription: AppSubscription;
  savedSettingsId?: number;
  savedSettingsName?: string;
  isUnsaved: boolean = false;
  gt7ChatWidgetExpanded: boolean = false;
  SubscriptionLevels = SubscriptionLevels;
  isAIEngineerSubscriptionExpired: boolean = true;

  dynoChart: Chart | undefined;
  shiftPointsChart: Chart | undefined;
  tractiveForceChart: Chart | undefined;

  suspensionLoading: boolean = false;
  drivetrainLoading: boolean = false;

  @ViewChild('screenshotSelector') fileUploader: ElementRef;
  @ViewChild('carTrackSettingsForm', { static: true }) carTrackSettingsForm!: NgForm;
  @ViewChild('suspensionSettingsForm', { static: true }) suspensionSettingsForm!: NgForm;
  @ViewChild('transmissionSettingsForm', { static: true }) transmissionSettingsForm!: NgForm;
  @ViewChild('dynoChart') dynoChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('shiftPointsChart') shiftPointsChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('tractiveForce') tractiveForceChartCanvas!: ElementRef<HTMLCanvasElement>;

  helpText: HelpText = {
    // car/track settings help text
    tireSize: { title: 'Tire Size', text: 'Enter the car\'s tire size. Use the tire size calculator to find the correct measurement.' },
    // suspension settings help text
    oversteerUndersteer: { title: 'Oversteer/Understeer', text: 'Adjust the amount of oversteer or understeer. Lower = more oversteer. Higher = more understeer. (This is user preference.)' },
    springStiffness: { title: 'Spring Stiffness', text: 'Adjust the stiffness of the springs. (This is user preference.)' },
    arbStiffness: { title: 'Anti-rollbar Stiffness', text: 'Adjust the stiffness of the Anti-rollbar. (This is user preference.)' },
    tireWear: { title: 'Tire Wear', text: 'Select tire wear rate.' },
    cornerEntry: { title: 'Reduce Corner Entry Understeer/Oversteer', text: 'Adjust the amount of corner entry oversteer or understeer. Negative = less entry oversteer. Positive = less entry understeer.' },
    cornerExit: { title: 'Reduce Corner Exit Understeer/Oversteer', text: 'Adjust the amount of corner exit oversteer or understeer. Negative = less exit oversteer. Positive = less exit understeer.' },
    offsetSpringFrequency: { title: 'Offset Spring Frequency', text: 'Adjust the offset of spring fequency front to rear. Negative = less oversteer. Positive = more oversteer.' },
    // transmission settings help text
    maxRPM: { title: 'Max RPM', text: 'Enter the max RPM shown on the dyno graph in car settings.' },
    minRPM: { title: 'Min RPM', text: 'Enter the min RPM shown on the dyno graph in car settings.' },
    maxTorque: { title: 'Max Torque', text: 'Enter the max torque show in car settings.' },
    maxPowerRPM: { title: 'RPM @ Max Power', text: 'RPM @ max power can be found by viewing the selected car in the garage.' },
    lsdSettings: { title: 'LSD Settings', text: 'Select limited slip differential setup. For cars with low rear grip, lower this setting.' },
    torqueDistribution: { title: 'Front Torque Distribution', text: 'Select the front torque distribution for 4WD cars.' },
    topSpeed: { title: 'Top Speed', text: 'Enter the expected top speed on the longest straight.' },
    finalGearCalc: { title: 'Final Gear Calculation', text: 'Select whether to calculate the final gear ratio or use a user defined final gear ratio. (Some cars may need a user defined value to get the gears in range.)' },
    gearRatiosCalc: { title: 'Gear Ratios Calculation', text: 'Select whether to calculate the gears or use user defined ratios.' },
    minCornerSpeed: { title: 'Min Corner Speed', text: 'Enter the desired minimum corner speed. (This can be raised slightly to provide peak torque further on the exit of the corner.)' },
    minCornerSpeedGear: { title: 'Min Corner Speed Gear', text: 'Enter the gear used at the desired minumum corner speed.' },
    ratioAdjuster: { title: 'Gear Ratio Adjuster % of RPM @ max', text: 'Alters the amount of space between gear ratios. 80% is the typical starting point.' }
  };


  private ngUnsubscribe = new Subject<void>();

  constructor(private modalService: NgbModal,
    private eventBroadcastService: EventBroadcastService, private userPreferencesService: UserPreferencesService,
    private digitizerService: DigitizerService, private chartService: ChartService, private protuneApiService: ProtuneApiService,
    private fb: FormBuilder, private router: Router, private authService: AuthService, private titleService: Title,
    private errorHandler: ErrorHandlerService) { }


  get carTrackSettingsFormControl() {
    return this.carTrackSettingsForm.controls;
  }

  get suspensionSettingsFormControl() {
    return this.suspensionSettingsForm.controls;
  }

  get drivetrainSettingsFormControl() {
    return this.transmissionSettingsForm.controls;
  }

  isElementVisible(selector: string) {
    const element = document.querySelector(selector);
    // Check for null/undefined first, then use checkVisibility() for display/visibility CSS
    return element && element.checkVisibility();
  }


  ngOnInit(): void {
    let carTrackSettings = localStorage.getItem('carTrackSettings');
    let suspensionSettings = localStorage.getItem('suspensionSettings');
    let drivetrainSettings = localStorage.getItem('drivetrainSettings');

    if (carTrackSettings) {
      this.carTrackSettings = JSON.parse(carTrackSettings);
    }

    if (suspensionSettings) {
      this.suspensionSettings = JSON.parse(suspensionSettings);
    }

    if (drivetrainSettings) {
      this.drivetrainSettings = JSON.parse(drivetrainSettings);
    }

    this.authService.getSubscription().subscribe({
      next: subscription => {
        this.subscription = subscription;

        if (this.subscription.aiEngineerSubscriptionExpiration != null) {
          this.isAIEngineerSubscriptionExpired = moment().isAfter(this.subscription.aiEngineerSubscriptionExpiration);
        }
      }
    });

    

    this.eventBroadcastService
      .userPreferencesUpdatedEvent
      .subscribe({
        next: (userPreferences: UserPreferences) => {
          this.userPreferences = userPreferences;
        }
        , error: (err: any) => {
          console.log(err);
        }
      });

    this.userPreferencesService.getUserPreferences()
      .subscribe({
        next: (preferences) => {
          this.userPreferences = preferences;
        }, error: err => this.errorHandler.handleError('Unable to get user preferences', err.error, err.req, true)
      });

    this.protuneApiService.getAllVehicles()
      .subscribe({
        next: vehicles => this.vehicles = vehicles.sort((a, b) => {
          return a.manufacturer.localeCompare(b.manufacturer);
        }),
        error: err => this.errorHandler.handleError('Unable to get vehicle list.', err.error, err.req, true)
      });

    this.carTrackSettingsForm.form.valueChanges.subscribe(value => {
      this.isUnsaved = true;
    });

    this.titleService.setTitle('GT Pro Tune - New');

    this.showNotifications();
  }

  ngDestroy(): void {
    this.eventBroadcastService
      .userPreferencesUpdatedEvent
      .unsubscribe();
  }


  createChart() {
    if (this.dynoChart != null) {
      this.dynoChart.destroy();
      this.dynoChartConfig = null;
    }

    if (this.shiftPointsChart != null) {
      this.shiftPointsChart.destroy();
      this.shiftPointsChartConfig = null;
    }

    if (this.tractiveForceChart != null) {
      this.tractiveForceChart.destroy();
      this.tractiveForceChartConfig = null;
    }

    if (this.drivetrainSettings.engineData != null && this.drivetrainSettings.engineData.length > 0) {
      let engineData = this.drivetrainSettings.engineData;
      let minRPM = this.drivetrainSettings.minRPM;
      let maxRPM = this.drivetrainSettings.maxRPM;



      this.dynoChartConfig = this.chartService
        .createDynoChartConfig(
          engineData,
          minRPM,
          maxRPM,
          this.userPreferences.torqueUnits,
          this.userPreferences.powerUnits);

      const ctxDynoChart = this.dynoChartCanvas.nativeElement.getContext('2d');

      this.dynoChart = new Chart(ctxDynoChart, this.dynoChartConfig);

      let selectedVehicle = this.carTrackSettings.vehicle; //this.vehicles.find(v => v.id == this.carTrackSettings.vehicleId);
      let driveWheels = selectedVehicle.driveType == 'FWD' ? 4 : 2;
      let tireSize = this.userPreferences.lengthUnits.display == LengthUnits.mm.display ? LengthUnits.convertMmToInches(this.carTrackSettings.tireSize) : this.carTrackSettings.tireSize;


      const ctxTractiveForceChart = this.tractiveForceChartCanvas.nativeElement.getContext('2d');

      this.tractiveForceChartConfig = this.chartService
        .createTractiveChartConfig(
          engineData,
          this.drivetrainSettings.gearRatios,
          this.drivetrainSettings.finalGear,
          driveWheels, tireSize,
          this.userPreferences.speedUnits);

      this.tractiveForceChart = new Chart(ctxTractiveForceChart, this.tractiveForceChartConfig);
    }


    if (this.carTrackSettings != null && this.drivetrain != null) {
      let lowerRPM = this.drivetrain.lowerRPM;
      let upperRPM = this.drivetrain.upperRPM;
      let shiftPoints = this.drivetrain.shiftPoints;

      const ctxShiftPointChart = this.shiftPointsChartCanvas.nativeElement.getContext('2d');

      this.shiftPointsChartConfig = this.chartService
        .createShiftPointsChartConfig(
          shiftPoints,
          lowerRPM,
          upperRPM,
          this.drivetrainSettings.maxRPM,
          this.drivetrain.maxSpeed,
          this.drivetrain.maxSpeedAtMaxPower,
          this.userPreferences.speedUnits,
          this.subscription);

      this.shiftPointsChart = new Chart(ctxShiftPointChart, this.shiftPointsChartConfig);
    }
  }

  setGear(gearNo: number, $event: Event) {
    let input = $event.target as HTMLInputElement;
    this.gearRatios[gearNo] = parseFloat(input.value);
  }


  async calculateSuspension() {
    this.suspensionLoading = true;
    if (this.carTrackSettingsForm.invalid || this.suspensionSettingsForm.invalid) {
      let errors = [];
      for (let key in this.carTrackSettingsForm.controls) {
        let control = this.carTrackSettingsForm.controls[key];
        for (let error in control.errors) {
          switch (error) {
            case 'required':
              errors.push(`${key} is required`);
          }
        }
      }

      for (let key in this.suspensionSettingsForm.controls) {
        let control = this.suspensionSettingsForm.controls[key];
        for (let error in control.errors) {
          switch (error) {
            case 'required':
              errors.push(`${key} is required`);
          }
        }
      }

      this.showError('Error Calculating Suspension', 'An error occurred calculating the suspension.', errors)
      this.suspensionLoading = false;
      return;
    }

    //gtag('event', 'calculate_suspension', {
    //  vehicleId: this.carTrackSettings.vehicle.id,
    //  vehicleModel: this.carTrackSettings.vehicle.model
    //});


    let selectedVehicle = this.carTrackSettings.vehicle; //this.vehicles.find(v => v.id == this.carTrackSettings.vehicleId);
    this.carTrackSettings.trackType = this.carTrackSettings.track.type == 'fast' ? 0 : 1;

    localStorage.setItem('carTrackSettings', JSON.stringify(this.carTrackSettings));
    localStorage.setItem('suspensionSettings', JSON.stringify(this.suspensionSettings));

    let si = { ...this.suspensionSettings };
    let cts = { ...this.carTrackSettings };

    let weightLbs = this.userPreferences.weightUnits.display == WeightUnits.kg.display ? WeightUnits.convertKgToLb(this.carTrackSettings.weight) : this.carTrackSettings.weight;
    let tireSizeIn = this.userPreferences.lengthUnits.display == LengthUnits.mm.display ? LengthUnits.convertMmToInches(this.carTrackSettings.tireSize) : this.carTrackSettings.tireSize;

    cts.weight = weightLbs;
    cts.tireSize = tireSizeIn;

    this.protuneApiService.getSuspensionSettings(si, cts, selectedVehicle)
      .subscribe({
        next: result => {
          this.suspension = result;
          this.suspensionSettings.downforceRear = this.suspension.downforceRear;
          this.suspensionLoading = false;
        },
        error: err => {
          this.suspensionLoading = false;
          this.errorHandler.handleError("Unable to get suspension settings.", err.error, err.req, true);
        }
      });
  }

  async calculateDrivetrain() {
    this.drivetrainLoading = true;

    if (this.carTrackSettingsForm.invalid || this.transmissionSettingsForm.invalid) {
      let errors = [];
      for (let key in this.carTrackSettingsForm.controls) {
        let control = this.carTrackSettingsForm.controls[key];
        for (let error in control.errors) {
          switch (error) {
            case 'required':
              errors.push(`${key} is required`);
          }
        }
      }

      for (let key in this.transmissionSettingsForm.controls) {
        let control = this.transmissionSettingsForm.controls[key];
        for (let error in control.errors) {
          switch (error) {
            case 'required':
              errors.push(`${key} is required`);
          }
        }
      }

      this.showError('Error Calculating Transmission', 'An error occurred calculating the transmission.', errors);
      this.drivetrainLoading = false;
      return;
    }

    gtag('event', 'calculate_drivetrain', {
      vehicleId: this.carTrackSettings.vehicle.id,
      vehicleModel: this.carTrackSettings.vehicle.model,
      hasScreenshot: this.screenshotFile != null
    });

    localStorage.setItem('carTrackSettings', JSON.stringify(this.carTrackSettings));
    localStorage.setItem('drivetrainSettings', JSON.stringify(this.drivetrainSettings));


    if (this.subscription.subscriptionLevel != 'Advanced' && this.subscription.subscriptionLevel != 'Trial') {
      this.drivetrainSettings.engineData = [];
    }

    //if (this.screenshotFile != null) {
    //  this.drivetrainSettings.engineData = await this.digitizerService
    //    .digitizeImage(
    //      this.screenshotFile,
    //      this.drivetrainSettings.minRPM,
    //      this.drivetrainSettings.maxRPM,
    //      this.drivetrainSettings.maxTorque,
    //      this.userPreferences);

    //  localStorage.setItem('drivetrainSettings', JSON.stringify(this.drivetrainSettings));
    //}
    //else {
    //  this.drivetrainSettings.engineData = this.drivetrainSettings.engineData || [];
    //}

    this.drivetrainSettings.engineData = this.drivetrainSettings.engineData || []

    let drivetrainSettings2 = { ...this.drivetrainSettings };
    let carTrackSettings2 = { ...this.carTrackSettings };

    //drivetrainSettings2.gearRatios = this.gearRatios;
    drivetrainSettings2.maxPower = PowerUnits.powerToHp(drivetrainSettings2.maxPower, this.userPreferences.powerUnits.display);
    drivetrainSettings2.maxTorque = TorqueUnits.torqueTolbft(drivetrainSettings2.maxTorque, this.userPreferences.torqueUnits.display);
    drivetrainSettings2.topSpeed = this.userPreferences.speedUnits.display == SpeedUnits.kmh.display ? SpeedUnits.speedToMph(drivetrainSettings2.topSpeed) : drivetrainSettings2.topSpeed;
    drivetrainSettings2.minCornerSpeed = this.userPreferences.speedUnits.display == SpeedUnits.kmh.display ? SpeedUnits.speedToMph(drivetrainSettings2.minCornerSpeed) : drivetrainSettings2.minCornerSpeed;
    carTrackSettings2.weight = this.userPreferences.weightUnits.display == WeightUnits.kg.display ? WeightUnits.convertKgToLb(this.carTrackSettings.weight) : this.carTrackSettings.weight;
    carTrackSettings2.vehicleId = carTrackSettings2.vehicle.id;
    carTrackSettings2.tireSize = this.userPreferences.lengthUnits.display == LengthUnits.mm.display ? LengthUnits.convertMmToInches(this.carTrackSettings.tireSize) : this.carTrackSettings.tireSize;

    if (carTrackSettings2.vehicle.driveType == DriveTypes.FF) {
      drivetrainSettings2.torqueDistribution = 100;
    }
    else if (carTrackSettings2.vehicle.driveType == DriveTypes.MR || this.carTrackSettings.vehicle.driveType == DriveTypes.RR || this.carTrackSettings.vehicle.driveType == DriveTypes.FR) {
      drivetrainSettings2.torqueDistribution = 0;
    }

    if (drivetrainSettings2.gearRatiosOption == 'calc') {
      drivetrainSettings2.gearRatios = null;
    }
    else {

      for (let i = drivetrainSettings2.gearCount + 1; i <= Object.values(this.drivetrainSettings.gearRatios).length; i++) {
        delete drivetrainSettings2.gearRatios[i];
      }
    }


    this.protuneApiService.getSettings(drivetrainSettings2, carTrackSettings2)
      .subscribe({
        next: (res) => {
          this.drivetrain = res;
          this.drivetrainSettings.gearRatios = {};
          Object.entries(res.gears).forEach(gear => {
            this.drivetrainSettings.gearRatios[gear[0]] = Math.round(gear[1].gearRatio * 1000) / 1000;
            this.drivetrainSettings.finalGear = res.finalGear;
          });
          this.drivetrainLoading = false;
          this.createChart();
        }, error: (err) => {
          this.drivetrainLoading = false;
          this.errorHandler.handleError('Unable to get transmission settings.', err.error, err.req, true);
          
        }
      });

  }

  new() {
    const modal = this.modalService.open(ConfirmationModalComponent);
    modal.componentInstance.title = "Confirm New";
    modal.componentInstance.message = "Are you sure you want to start a new tune?";

    modal.closed.subscribe(result => {
      if (result === true) {
        //this.carTrackSettingsForm.resetForm();
        //this.suspensionSettingsForm.resetForm();
        //this.transmissionSettingsForm.resetForm();
        localStorage.removeItem('carTrackSettings');
        localStorage.removeItem('drivetrainSettings');
        localStorage.removeItem('suspensionSettings');

        localStorage.removeItem('carTrackSettings');
        localStorage.removeItem('suspensionSettings');
        localStorage.removeItem('drivetrainSettings');

        this.savedSettingsId = null;
        this.savedSettingsName = null;
        this.carTrackSettings = new CarTrackSettings();
        this.suspensionSettings = new SuspensionSettings();
        this.drivetrainSettings = new DrivetrainSettings();
        this.suspension = null;
        this.drivetrain = null;
        this.dynoChartConfig = null;
        this.tractiveForceChartConfig = null;
        this.shiftPointsChartConfig = null;
        //this.vehicles = null;
        this.titleService.setTitle('GT Pro Tune - New');
      }
    });

  }

  load() {
    const modal = this.modalService.open(SavedSettingsModalComponent, {
      size: 'lg'
    });
    modal.componentInstance.mode = SavedSettingsMode.load

    modal.closed
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: SavedSettings) => {
          this.savedSettingsId = result.id;
          this.savedSettingsName = result.name;

          this.suspension = null;
          this.drivetrain = null;
          this.dynoChartConfig = null;
          this.tractiveForceChartConfig = null;
          this.shiftPointsChartConfig = null;
          this.carTrackSettings = JSON.parse(result.carTrackSettings);
          this.suspensionSettings = JSON.parse(result.suspensionSettings);
          this.drivetrainSettings = JSON.parse(result.drivetrainSettings);

          this.drivetrainSettings.gearRatioMode = this.drivetrainSettings.gearRatioMode ?? GearingMode.MaxTorque;

          if (this.carTrackSettings.vehicle != null) {
            this.carTrackSettings.vehicle = this.vehicles.find(v => v.id == this.carTrackSettings.vehicle.id);
          }
          else if (this.carTrackSettings.vehicleId != null) {
            this.carTrackSettings.vehicle = this.vehicles.find(v => v.id == this.carTrackSettings.vehicleId);
          }

          if (!this.carTrackSettingsForm.invalid && !this.suspensionSettingsForm.invalid) {
            this.calculateSuspension();
          }

          if (!this.carTrackSettingsForm.invalid && !this.transmissionSettingsForm.invalid) {
            this.calculateDrivetrain();
          }


          this.isUnsaved = false;
          this.titleService.setTitle(`GT Pro Tune - ${this.savedSettingsName}`);
          gtag('event', 'load_saved_settings');
        }
      });
  }

  save(saveAs: boolean = false) {
    if (this.savedSettingsId == null || saveAs == true) {
      const modal = this.modalService.open(SavedSettingsModalComponent, {
        size: 'lg'
      });
      modal.componentInstance.mode = SavedSettingsMode.save;
      modal.componentInstance.carTrackSettings = this.carTrackSettings;
      modal.componentInstance.suspensionSettings = this.suspensionSettings;
      modal.componentInstance.drivetrainSettings = this.drivetrainSettings;

      modal.closed.subscribe(result => {
        this.savedSettingsId = result.savedSettingsId;
        this.savedSettingsName = result.name;
        this.isUnsaved = false;
        this.titleService.setTitle(`GT Pro Tune - ${this.savedSettingsName}`);
      })
    }
    else {
      this.protuneApiService.saveSettings(this.savedSettingsId, this.savedSettingsName, this.carTrackSettings, this.suspensionSettings, this.drivetrainSettings)
        .subscribe({
          next: (savedSettingsId: number) => {
            this.isUnsaved = false;
            console.log('Saved successfully')
          }, error: err => this.errorHandler.handleError('Unable to save settings.', err.error, err.req, true)
        });
    }
    gtag('event', 'save_settings', {
      vehicleId: this.carTrackSettings.vehicle.id,
      vehicleModel: this.carTrackSettings.vehicle.model
    });
  }

  gearsToArray() {
    if (this.drivetrain) {
      return Object.values(this.drivetrain.gears)
        .map(k => k)
    } else {
      return [];
    }
  }

  showFrontDifferential() {
    if (this.drivetrain) {
      let driveType = this.drivetrain.driveType;
      return driveType == 'FWD' || driveType == 'FF';
    }

    return false;
  }

  showRearDifferential() {
    if (this.drivetrain) {
      let driveType = this.drivetrain.driveType;
      return driveType == 'FWD' || driveType == 'MR' || driveType == 'FR' || driveType == 'RR';
    }

    return false;
  }

  numSequence(a: number, b: number, increment: number = 1): Array<number> {
    var arr = [];

    for (var i = a; i <= b; i += increment) {
      arr.push(i);
    }

    return arr;
  }

  showError(title: string, message?: string, errors: Array<string> = []) {
    const modal = this.modalService.open(ErrorModalComponent);
    modal.componentInstance.title = title;
    modal.componentInstance.message = message;
    modal.componentInstance.errors = errors;
  }

  showScreenshotHelpModal() {
    const modal = this.modalService
      .open(ScreenshotHelpModalComponent, {
        size: 'xl',
        fullscreen: 'lg'
      });
  }

  showTireCalculatorModal() {
    const modal = this.modalService
      .open(TireSizeCalculatorComponent)
      .closed.subscribe({
        next: (tireSize: number) => {
          this.carTrackSettings.tireSize = tireSize;
        }
      });
  }

  gearRatiosOptionChanged() {
    if (this.drivetrainSettings.gearRatiosOption == 'user') {
      this.drivetrainSettings.gearRatios = this.drivetrainSettings.gearRatios || {};

    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  showNotifications() {
    this.protuneApiService.getAppNotifications()
      .subscribe({
        next: (notifications) => {
          if (notifications && notifications.length > 0) {
            const modal = this.modalService.open(NotificationModalComponent, { size: 'lg' });
            modal.componentInstance.notifications = notifications;

          }
        }
      })

  }

  toggleChat() {
    this.gt7ChatWidgetExpanded = !this.gt7ChatWidgetExpanded;
  }

  getCurrentSetupState() {
    return {
      vehicle: this.carTrackSettings.vehicle,
      weight: this.carTrackSettings.weight,
      trackType: this.carTrackSettings.track.type,
      frontTires: this.carTrackSettings.frontTires,
      rearTires: this.carTrackSettings.rearTires,
      performancePoints: this.carTrackSettings.performancePoints,
      tireSize: this.carTrackSettings.tireSize,

      // Suspension settings
      rideHeightFront: this.suspensionSettings.rideHeightFront,
      rideHeightRear: this.suspensionSettings.rideHeightRear,
      downforceFront: this.suspensionSettings.downforceFront,
      downforceRear: this.suspensionSettings.downforceRear,
      weightDistributionPercent: this.suspensionSettings.weightDistributionPercent,
      oversteerUndersteer: this.suspensionSettings.oversteerUndersteer,
      springStiffness: this.suspensionSettings.springStiffness,
      arbStiffness: this.suspensionSettings.arbStiffness,

      // Complete Drivetrain settings including individual gear ratios
      drivetrainSettings: {
        maxPower: this.drivetrainSettings.maxPower,
        maxTorque: this.drivetrainSettings.maxTorque,
        maxRPM: this.drivetrainSettings.maxRPM,
        topSpeed: this.drivetrainSettings.topSpeed,
        gearCount: this.drivetrainSettings.gearCount,
        lsdSetting: this.drivetrainSettings.lsdSetting,
        gearRatioMode: this.drivetrainSettings.gearRatioMode,

        // Individual gear ratios (CRITICAL for gear-related questions)
        finalGear: this.drivetrainSettings.finalGear,
        gearRatios: this.drivetrainSettings.gearRatios || {},
        gear1: this.drivetrainSettings.gearRatios?.['1'] || 0,
        gear2: this.drivetrainSettings.gearRatios?.['2'] || 0,
        gear3: this.drivetrainSettings.gearRatios?.['3'] || 0,
        gear4: this.drivetrainSettings.gearRatios?.['4'] || 0,
        gear5: this.drivetrainSettings.gearRatios?.['5'] || 0,
        gear6: this.drivetrainSettings.gearRatios?.['6'] || 0,
        gear7: this.drivetrainSettings.gearRatios?.['7'] || 0,
        gear8: this.drivetrainSettings.gearRatios?.['8'] || 0,

        // Available drivetrain properties only
        torqueDistribution: this.drivetrainSettings.torqueDistribution,
        finalGearOption: this.drivetrainSettings.finalGearOption,
        gearRatiosOption: this.drivetrainSettings.gearRatiosOption,
        minCornerSpeed: this.drivetrainSettings.minCornerSpeed,
        minCornerSpeedGear: this.drivetrainSettings.minCornerSpeedGear,
        ratioAdjuster: this.drivetrainSettings.ratioAdjuster,
        engineData: this.drivetrainSettings.engineData || []
      },

      // Complete Suspension settings (using actual SuspensionSettings properties)
      suspensionSettings: {
        weightDistributionPercent: this.suspensionSettings.weightDistributionPercent,
        lowSpeedStability: this.suspensionSettings.lowSpeedStability,
        highSpeedStability: this.suspensionSettings.highSpeedStability,
        rotationalG1: this.suspensionSettings.rotationalG1,
        rotationalG2: this.suspensionSettings.rotationalG2,
        rotationalG3: this.suspensionSettings.rotationalG3,
        rideHeightFront: this.suspensionSettings.rideHeightFront,
        rideHeightRear: this.suspensionSettings.rideHeightRear,
        downforceFront: this.suspensionSettings.downforceFront,
        downforceRear: this.suspensionSettings.downforceRear,
        calculateRearDownforce: this.suspensionSettings.calculateRearDownforce,
        springStiffness: this.suspensionSettings.springStiffness,
        arbStiffness: this.suspensionSettings.arbStiffness,
        tireWear: this.suspensionSettings.tireWear,
        oversteerUndersteer: this.suspensionSettings.oversteerUndersteer,
        reduceCornerEntryUndersteerOversteer: this.suspensionSettings.reduceCornerEntryUndersteerOversteer,
        reduceCornerExitUndersteerOversteer: this.suspensionSettings.reduceCornerExitUndersteerOversteer,
        offsetSpringFrequency: this.suspensionSettings.offsetSpringFrequency
      },

      // Current results
      suspension: this.suspension,
      drivetrain: this.drivetrain,

      // Settings name
      savedSettingsName: this.savedSettingsName
    };
  }


  openProcessImageModal() {
    const modal = this.modalService
      .open(ProcessImageModalComponent, {
        size: 'xl',
        backdrop: 'static',
        modalDialogClass: 'image-analyzer-modal'
      });

    modal.closed.subscribe((result: ProcessImageResult) => {
      if (result.acceptParameters) {
        this.carTrackSettings.vehicle = result.analyzedImageData.vehicle ?? this.carTrackSettings.vehicle;
        this.carTrackSettings.vehicleId = result.analyzedImageData.vehicleId ?? this.carTrackSettings.vehicleId;
        this.carTrackSettings.performancePoints = result.analyzedImageData.performancePoints ?? this.carTrackSettings.performancePoints;
        this.carTrackSettings.weight = result.analyzedImageData.weight ?? this.carTrackSettings.weight;
        this.carTrackSettings.tireSize = parseFloat(result.analyzedImageData.wheelSize.toFixed(2)) ?? this.carTrackSettings.tireSize;
        this.carTrackSettings.frontTires = result.analyzedImageData.frontTires ?? this.carTrackSettings.frontTires;
        this.carTrackSettings.rearTires = result.analyzedImageData.rearTires ?? this.carTrackSettings.rearTires;

        this.suspensionSettings.weightDistributionPercent = result.analyzedImageData.weightDistributionPercent ?? this.suspensionSettings.weightDistributionPercent;
        this.suspensionSettings.lowSpeedStability = result.analyzedImageData.lowSpeed ?? this.suspensionSettings.lowSpeedStability;
        this.suspensionSettings.highSpeedStability = result.analyzedImageData.highSpeed ?? this.suspensionSettings.highSpeedStability;
        this.suspensionSettings.rotationalG1 = result.analyzedImageData.rotationalG1 ?? this.suspensionSettings.rotationalG1;
        this.suspensionSettings.rotationalG2 = result.analyzedImageData.rotationalG2 ?? this.suspensionSettings.rotationalG2;
        this.suspensionSettings.rotationalG3 = result.analyzedImageData.rotationalG3 ?? this.suspensionSettings.rotationalG3;
        this.suspensionSettings.rideHeightFront = result.analyzedImageData.bodyHeightFront ?? this.suspensionSettings.rideHeightFront;
        this.suspensionSettings.rideHeightRear = result.analyzedImageData.bodyHeightRear ?? this.suspensionSettings.rideHeightRear;
        this.suspensionSettings.downforceFront = result.analyzedImageData.downforceFront ?? this.suspensionSettings.downforceFront;
        this.suspensionSettings.downforceRear = result.analyzedImageData.downforceRear ?? this.suspensionSettings.downforceRear;

        this.drivetrainSettings.maxPower = result.analyzedImageData.maxPower ?? this.drivetrainSettings.maxPower;
        this.drivetrainSettings.maxTorque = result.analyzedImageData.maxTorque ?? this.drivetrainSettings.maxTorque;
        this.drivetrainSettings.minRPM = result.analyzedImageData.minRPM ?? this.drivetrainSettings.minRPM;
        this.drivetrainSettings.maxRPM = result.analyzedImageData.maxRPM ?? this.drivetrainSettings.maxRPM;
        this.drivetrainSettings.finalGear = result.analyzedImageData.finalGearRatio ?? this.drivetrainSettings.finalGear;
      }

      if (result.acceptEngineData) {
        this.drivetrainSettings.engineData = result.analyzedImageData.engineData ?? this.drivetrainSettings.engineData;
      }

    });
  }

  clearEngineData() {
    this.drivetrainSettings.engineData = [];
  }
}
